import { ref, computed, onUnmounted, type Ref } from 'vue'
import { Device, Call } from '@twilio/voice-sdk'
import { useUser } from './useUser'

const COMM_API_URL = import.meta.env.VITE_COMM_API_URL
const COMM_API_LOG_LEVEL = import.meta.env.VITE_COMM_API_LOG_LEVEL

let device: Device | null = null
let currentCall: Call | null = null
let initPromise: Promise<void> | null = null

const initialized: Ref<boolean> = ref(false)
const deviceReady: Ref<boolean> = ref(false)
const deviceError: Ref<string> = ref('')
const callInProgress: Ref<boolean> = ref(false)
const callIsMuted: Ref<boolean> = ref(false)
const callIncoming: Ref<boolean> = ref(false)
const callerFrom: Ref<string> = ref('')
const callStartedAt: Ref<Date | null> = ref(null)
const callDuration: Ref<number> = ref(0)

let durationInterval: ReturnType<typeof setInterval> | null = null

function startDurationTimer() {
  callStartedAt.value = new Date()
  callDuration.value = 0
  durationInterval = setInterval(() => {
    if (callStartedAt.value) {
      callDuration.value = Math.floor((Date.now() - callStartedAt.value.getTime()) / 1000)
    }
  }, 1000)
}

function stopDurationTimer() {
  if (durationInterval) {
    clearInterval(durationInterval)
    durationInterval = null
  }
  callStartedAt.value = null
  callDuration.value = 0
}

function resetCallState() {
  callInProgress.value = false
  callIncoming.value = false
  callIsMuted.value = false
  callerFrom.value = ''
  currentCall = null
  stopDurationTimer()
}

function setCallListeners(call: Call) {
  call.on('accept', () => {
    callInProgress.value = true
    callIsMuted.value = false
    callIncoming.value = true
    startDurationTimer()
  })
  call.on('cancel', () => resetCallState())
  call.on('reject', () => resetCallState())
  call.on('disconnect', () => resetCallState())
  call.on('mute', (isMuted: boolean) => {
    callIsMuted.value = isMuted
  })
}

async function fetchTwilioToken(accessToken: string): Promise<string> {
  const res = await fetch(`${COMM_API_URL}/twilio/token/`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  })
  if (!res.ok) throw new Error('Impossible de récupérer le token Twilio')
  return (await res.json()).token
}

export function useCall() {
  const { data: user } = useUser()

  async function initDevice() {
    if (device || initPromise) return

    const idToken = user.value?.id_token
    if (!idToken) return

    initPromise = _doInitDevice(idToken)
    await initPromise
  }

  async function _doInitDevice(idToken: string) {

    try {
      const twilioToken = await fetchTwilioToken(idToken)
      if (!twilioToken) return

      device = new Device(twilioToken, {
        logLevel: COMM_API_LOG_LEVEL || 'warn',
        appName: 'tbb_dashboard',
        closeProtection: true,
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
        enableImprovedSignalingErrorPrecision: true,
      })

      device.addListener('tokenWillExpire', async () => {
        try {
          const token = user.value?.id_token
          if (!token) return
          const newTwilioToken = await fetchTwilioToken(token)
          await device!.updateToken(newTwilioToken)
        } catch (e: unknown) {
          deviceError.value = e instanceof Error ? e.message : String(e)
        }
      })

      device.addListener('registered', () => {
        deviceError.value = ''
        initialized.value = true
      })

      device.addListener('ready', () => {
        deviceReady.value = true
      })

      device.addListener('error', (twilioError: { message: string }) => {
        deviceError.value = twilioError.message
      })

      device.addListener('disconnect', () => {
        deviceError.value = ''
      })

      device.addListener('connect', () => {
        deviceError.value = ''
      })

      device.addListener('incoming', (call: Call) => {
        currentCall = call
        callIncoming.value = true
        callerFrom.value = call.parameters?.From || ''
        setCallListeners(call)
      })

      device.register()
    } catch (e: unknown) {
      deviceError.value = e instanceof Error ? e.message : String(e)
    }
  }

  async function acceptIncomingCall() {
    if (currentCall) {
      currentCall.accept()
    }
  }

  async function emitCall(target: string) {
    if (!device) return
    try {
      const call = await device.connect({
        params: { To: target },
        rtcConstraints: { audio: true },
      })
      currentCall = call
      callInProgress.value = true
      callIncoming.value = false
      callerFrom.value = target
      setCallListeners(call)
      startDurationTimer()
    } catch (e: unknown) {
      deviceError.value = e instanceof Error ? e.message : String(e)
    }
  }

  function disconnectCall() {
    if (currentCall) {
      currentCall.disconnect()
    }
  }

  function ignoreCall() {
    if (currentCall) {
      currentCall.ignore()
      resetCallState()
    }
  }

  function muteCall() {
    if (currentCall) {
      currentCall.mute(!callIsMuted.value)
    }
  }

  function destroyDevice() {
    if (device) {
      device.destroy()
      device = null
      initialized.value = false
      deviceReady.value = false
      resetCallState()
    }
  }

  const formattedDuration = computed(() => {
    const total = callDuration.value
    const m = Math.floor(total / 60)
    const s = total % 60
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  })

  onUnmounted(() => {
    stopDurationTimer()
  })

  return {
    initialized,
    deviceReady,
    deviceError,
    callInProgress,
    callIsMuted,
    callIncoming,
    callerFrom,
    callStartedAt,
    callDuration,
    formattedDuration,

    initDevice,
    acceptIncomingCall,
    emitCall,
    disconnectCall,
    ignoreCall,
    muteCall,
    destroyDevice,
  }
}
